import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { BroadcastEventType, IBroadcastEvent, StoreKeys } from '../../models/broadcast.model';
import { TokenRefreshService } from '../auth/token-refresh.service';
import { SESSION_TIMEOUT } from '../../constants/system-constants.model';
import { filter, timer } from 'rxjs';
import { AuthState } from '../../store/auth/auth.state';
import { AuthService } from '../auth/auth.service';
import { LanguageType } from '../../constants/language-eum';
import { LanguageService } from '../translation/language.service';
import { FormTrackerService } from '../common/form-tracker.service';
import { SettingService } from '../settings/setting.service';
import { SLATimerService } from '../common/sla-timer.service';

@Injectable({
  providedIn: 'root'
})
export class BroadCastChannelService {
  private store = inject(Store);
  private router = inject(Router);
  private tokenRefreshService = inject(TokenRefreshService);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);
  private formTrackerService = inject(FormTrackerService);
  private settingService = inject(SettingService);

  channel = new BroadcastChannel('tpa-channel');

  private tabId = crypto.randomUUID();
  private isAnyTabDirty = false;
  private slaTimerService = inject(SLATimerService);

  constructor() {
    this.languageService.reloadAllTabsEvent$.pipe(filter(language => !!language)).subscribe((language: LanguageType) => {
      this.reloadAllTabs(language);
    });

    this.languageService.displayDiffLanguageEvent$.subscribe(() => {
      this.channel.postMessage({ eventFor: BroadcastEventType.LANGUAGE_CONFIRMATION_DIALOG });
    });

    this.languageService.closeLanguageDialogEvent$.subscribe(() => {
      this.channel.postMessage({ eventFor: BroadcastEventType.LANGUAGE_DIALOG_CONFIRMED });
    });

    this.channel.onmessage = (event: MessageEvent<IBroadcastEvent>) => {
      const data = event.data;
      if (!data || data.sender === this.tabId) return;

      if (data.eventFor === BroadcastEventType.LOGIN)
        this.handleLoginEvent();
      else if (data.eventFor === BroadcastEventType.LOGOUT)
        this.handleLogoutEvent(data.isPatientUser);
      else if (data.eventFor === BroadcastEventType.CONTINUE)
        this.handleContinueEvent();
      else if (data.eventFor === BroadcastEventType.STORE)
        this.syncAllAppState(event);
      else if (data.eventFor === BroadcastEventType.DIRTY_STATE_REQUEST) {
        this.channel.postMessage({
          eventFor: BroadcastEventType.DIRTY_STATE_RESPONSE,
          isDirty: this.formTrackerService.isDirty(),
          sender: this.tabId
        });
      } else if (data.eventFor === BroadcastEventType.DIRTY_STATE_RESPONSE) {
        if (data.isDirty) this.isAnyTabDirty = true;
      } else if (data.eventFor === BroadcastEventType.LANGUAGE_SYNC) {
        if (window.__appBeforeUnloadHandler) {
          window.removeEventListener('beforeunload', window.__appBeforeUnloadHandler);
        }
        this.languageService.setLanguage(data.language as LanguageType, true, true, 0);
      } else if (data.eventFor === BroadcastEventType.LANGUAGE_CONFIRMATION_DIALOG)
        this.languageService.displayConfirmLanguageDialog(false);
      else if (data.eventFor === BroadcastEventType.LANGUAGE_DIALOG_CONFIRMED)
        this.languageService.closeLanguageDialog(false);
      else if (data.eventFor === BroadcastEventType.DASHBOARD_SETTING_UPDATE)
        this.settingService.isDynamicChartVisible.next(data.isDynamicChartVisible);
      else if (data.eventFor === BroadcastEventType.PAYER_SETTINGS_UPDATE)
        this.settingService.isPayerSettingChange.next(data.isPayerSettingChange);
      else if (data.eventFor === BroadcastEventType.SLA_TIMER_PAUSE) {
        if (data.approvalRequestId)
          this.slaTimerService.triggerSlaTimer(data.approvalRequestId, 'PAUSE');
      } else if (data.eventFor === BroadcastEventType.SLA_TIMER_RESUME) {
        if (data.approvalRequestId)
          this.slaTimerService.triggerSlaTimer(data.approvalRequestId, 'RESUME');
      } else if (data.eventFor === BroadcastEventType.SLA_TIMER_SYNC_REQUEST) {
        this.slaTimerService.triggerSlaTimerSyncRequest(data.approvalRequestId);
      } else if (data.eventFor === BroadcastEventType.SLA_TIMER_SYNC_RESPONSE) {
        if (data.approvalRequestId) {
          this.slaTimerService.triggerSlaTimerSyncResponse({
            approvalRequestId: data.approvalRequestId,
            timerSeconds: data.timerSeconds,
            isTimerRunning: data.isTimerRunning
          });
        }
      }
    };

    window.addEventListener('unload', this.cleanup);
  }

  handleLoginEvent() {
    this.authService.redirectToDashboard(this.store.selectSnapshot(AuthState.isPatientUser));
  }

  handleLogoutEvent(isPatientUser: boolean) {
    timer(5).subscribe(() => {
      this.router.navigate([isPatientUser ? '/patient-login' : '/login']);
      this.tokenRefreshService.stopTimer();
    });
  }

  handleContinueEvent() {
    this.tokenRefreshService.setSessionTimeout(SESSION_TIMEOUT.CONTINUE);
  }

  syncAllAppState(event: MessageEvent<IBroadcastEvent>) {
    const newState = event.data.clonedState;
    const key = event.data.value as string;
    const state = this.store.snapshot();
    this.store.reset(key == StoreKeys.ALL ? newState : { ...state, [key]: newState?.[key] });
  }

  broadcastState(value: string, isPatientUser: boolean = false) {
    const state = this.store.snapshot();
    const clonedState = JSON.parse(JSON.stringify(state));
    this.postMessage({ clonedState, value, eventFor: BroadcastEventType.STORE, isPatientUser: isPatientUser });
  }

  broadCastEvent(eventFor: BroadcastEventType, isPatientUser: boolean = false) {
    this.postMessage({ eventFor, isPatientUser: isPatientUser });
  }

  requestSlaTimerSync(approvalRequestId: string) {
    this.postMessage({
      eventFor: BroadcastEventType.SLA_TIMER_SYNC_REQUEST,
      approvalRequestId,
      sender: this.tabId
    });
  }

  respondSlaTimerSync(approvalRequestId: string, timerSeconds: number, isTimerRunning: boolean) {
    this.postMessage({
      eventFor: BroadcastEventType.SLA_TIMER_SYNC_RESPONSE,
      approvalRequestId,
      timerSeconds,
      isTimerRunning,
      sender: this.tabId
    });
  }

  broadcastSlaTimer(eventFor: BroadcastEventType, approvalRequestId: string) {
    this.postMessage({
      eventFor: eventFor,
      approvalRequestId,
      sender: this.tabId
    });
  }

  postMessage(eventFor: unknown) {
    try {
      this.channel.postMessage(eventFor);
    } catch (e) {
      console.warn('Failed to send message via BroadcastChannel: tpa-channel', e);
    }
  }

  anyTabDirty(callback: (dirty: boolean) => void) {
    this.isAnyTabDirty = false;
    const handler = (event: MessageEvent<any>) => {
      const d = event.data;
      if (d.eventFor === BroadcastEventType.DIRTY_STATE_RESPONSE && d.isDirty) {
        this.isAnyTabDirty = true;
      }
    };

    this.channel.addEventListener('message', handler);

    this.channel.postMessage({
      eventFor: BroadcastEventType.DIRTY_STATE_REQUEST,
      sender: this.tabId
    });

    setTimeout(() => {
      this.channel.removeEventListener('message', handler);
      callback(this.isAnyTabDirty);
    }, 50);
  }

  reloadAllTabs(language: LanguageType) {
    this.channel.postMessage({
      eventFor: BroadcastEventType.LANGUAGE_SYNC,
      language,
      sender: this.tabId
    });
    this.languageService.setLanguage(language, true, true, 20);
  }

  resetDashboardSetting(isDynamicChartVisible: boolean) {
    this.channel.postMessage({ eventFor: BroadcastEventType.DASHBOARD_SETTING_UPDATE, isDynamicChartVisible: isDynamicChartVisible });
  }

  resetPayerSetting(isPayerSettingChange: boolean) {
    this.channel.postMessage({ eventFor: BroadcastEventType.PAYER_SETTINGS_UPDATE, isPayerSettingChange: isPayerSettingChange });
  }

  private cleanup = () => {
    if (this.channel) {
      this.channel.close();
    }
  };

}
